import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../api";
import { useAuth } from "../auth";
import { Check, ArrowRight } from "lucide-react";
import { Logo } from "../components/Logo";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const onboarding = params.get("onboarding") === "1";

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans));
  }, []);

  // Analytics — enregistre la visite Tarifs si utilisateur connecté (fire-and-forget)
  useEffect(() => {
    if (user?.id) {
      api.post("/analytics/tarifs-visit").catch(() => { /* silence : jamais bloquant */ });
    }
  }, [user?.id]);

  const choose = async (planId) => {
    setError("");
    if (planId === "decouverte") {
      if (user) nav("/carnet/identite");
      else nav("/inscription");
      return;
    }
    // Pour tous les plans payants, on redirige vers le Stripe Payment Link fourni
    const p = plans.find((x) => x.id === planId);
    const link = p?.stripe_payment_link_monthly;
    if (!link) {
      setError("Lien de paiement indisponible pour ce forfait. Merci de réessayer plus tard.");
      return;
    }
    // Analytics — enregistre le clic checkout si connecté (fire-and-forget)
    if (user?.id) {
      api.post("/analytics/checkout-started", { plan_id: planId }).catch(() => {});
    }
    // Prefill email if user connected (Stripe Payment Link supports ?prefilled_email=)
    let url = link;
    if (user?.email) {
      const sep = url.includes("?") ? "&" : "?";
      url = `${url}${sep}prefilled_email=${encodeURIComponent(user.email)}`;
    }
    setLoading(true);
    // Open Stripe in same tab (checkout return automatically redirects to success)
    window.location.href = url;
  };

  return (
    <div className="min-h-screen px-6 md:px-12 py-12" style={{ backgroundColor: "var(--auprea-cream)" }}>
      <div className="flex items-center justify-between mb-12 max-w-7xl mx-auto">
        <Link to="/"><Logo size={28} /></Link>
        {!user && (
          <Link to="/connexion"><button className="auprea-btn-outline" style={{ padding: "10px 22px", minHeight: "44px" }}>Se connecter</button></Link>
        )}
        {user && <Link to="/carnet/identite" style={{ color: "var(--auprea-blue)", fontSize: 14 }}>← Retour au carnet</Link>}
      </div>

      <div className="max-w-5xl mx-auto text-center">
        {onboarding && (
          <div style={{ color: "var(--auprea-green)", fontSize: 14, fontWeight: 500, marginBottom: 16 }}>
            <Check size={18} style={{ display: "inline", marginRight: 8 }} />
            Compte créé ! Choisissez votre forfait pour commencer.
          </div>
        )}
        <div style={{ color: "var(--auprea-gold)", fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 16 }}>
          — Forfaits
        </div>
        <h1 style={{ fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)", marginBottom: 16 }}>
          Un forfait pour chaque étape de la vie.
        </h1>
        <p style={{ fontSize: 18, color: "rgba(35,38,46,0.7)", maxWidth: 640, margin: "0 auto" }}>
          Commencez gratuitement avec Découverte. Évoluez à votre rythme vers Essentiel, Confort ou Patrimoine+.
        </p>

        {/* Cycle toggle — masqué tant qu'un seul cycle (mensuel) est actif */}
        {false && (
        <div className="flex justify-center gap-1 mt-10 mb-12" style={{ backgroundColor: "rgba(21,41,79,0.08)", padding: 4, width: "fit-content", margin: "40px auto 48px" }}>
          {[
            { id: "monthly", label: "Mensuel" },
            { id: "yearly", label: "Annuel (2 mois offerts)" },
          ].map((c) => (
            <button
              key={c.id}
              onClick={() => setCycle(c.id)}
              data-testid={`cycle-${c.id}`}
              style={{
                padding: "10px 24px",
                background: cycle === c.id ? "var(--auprea-navy)" : "transparent",
                color: cycle === c.id ? "var(--auprea-cream)" : "var(--auprea-navy)",
                fontSize: 14, fontWeight: 500,
              }}
            >
              {c.label}
            </button>
          ))}
        </div>
        )}
        <div style={{ marginTop: 32 }} />
      </div>

      {error && (
        <div style={{ maxWidth: 720, margin: "0 auto 24px", color: "#b53a3a", padding: "12px 16px", backgroundColor: "#fcf3f3", border: "1px solid #f0c5c5", textAlign: "center" }} data-testid="pricing-error">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
        {plans.map((p) => {
          const isRecommande = p.id === "patrimoine_plus";
          const price = cycle === "yearly" ? p.price_yearly : p.price_monthly;
          const cycleSuffix = cycle === "yearly" ? "/an" : "/mois";
          return (
            <div
              key={p.id}
              className="p-6 flex flex-col"
              style={{
                backgroundColor: isRecommande ? "var(--auprea-navy)" : "#fff",
                color: isRecommande ? "var(--auprea-cream)" : "var(--auprea-ink)",
                border: isRecommande ? "none" : "1px solid rgba(202,168,106,0.4)",
                position: "relative",
              }}
              data-testid={`plan-${p.id}`}
            >
              {isRecommande && (
                <div style={{ position: "absolute", top: -14, left: 20, background: "var(--auprea-gold)", color: "var(--auprea-navy)", padding: "4px 12px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  Recommandé
                </div>
              )}
              <div style={{ fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--auprea-gold)", fontWeight: 600 }}>
                {p.name}
              </div>
              <div style={{ fontFamily: "Spectral, serif", fontSize: 15, marginTop: 8, color: isRecommande ? "rgba(243,239,230,0.8)" : "rgba(35,38,46,0.7)", minHeight: 48 }}>
                {p.tagline}
              </div>
              <div className="my-6">
                <span style={{ fontFamily: "Spectral, serif", fontSize: 46, fontWeight: 300, lineHeight: 1 }}>
                  {price === 0 ? "Gratuit" : `${price.toFixed(2).replace(".", ",")} €`}
                </span>
                {price > 0 && <span style={{ marginLeft: 6, fontSize: 15, color: isRecommande ? "rgba(243,239,230,0.7)" : "rgba(35,38,46,0.6)" }}>{cycleSuffix}</span>}
              </div>
              <ul className="space-y-3 flex-1 mb-6">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check size={16} color={isRecommande ? "var(--auprea-gold)" : "var(--auprea-green)"} style={{ flexShrink: 0, marginTop: 3 }} />
                    <span style={{ fontSize: 14, color: isRecommande ? "var(--auprea-cream)" : "var(--auprea-ink)", lineHeight: 1.45 }}>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => choose(p.id)}
                disabled={loading}
                className="w-full"
                style={{
                  padding: "12px 20px", fontSize: 15, fontWeight: 500, minHeight: 46,
                  background: isRecommande ? "var(--auprea-gold)" : "var(--auprea-blue)",
                  color: isRecommande ? "var(--auprea-navy)" : "var(--auprea-cream)",
                  border: "none", cursor: loading ? "not-allowed" : "pointer",
                }}
                data-testid={`btn-choose-${p.id}`}
              >
                {p.id === "decouverte" ? "Commencer gratuitement" : (loading ? "Redirection…" : "Choisir ce forfait")}
                <ArrowRight size={16} style={{ display: "inline", marginLeft: 8 }} />
              </button>
            </div>
          );
        })}

        {/* Feuille de route Succession — paiement unique, hors abonnement */}
        <div
          className="p-6 flex flex-col"
          style={{
            backgroundColor: "#123B36",
            color: "#F6F2E9",
            border: "none",
            position: "relative",
          }}
          data-testid="plan-succession-feuille-route"
        >
          <div style={{ position: "absolute", top: -14, left: 20, background: "#AD7C33", color: "#123B36", padding: "4px 12px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Paiement unique
          </div>
          <div style={{ fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase", color: "#AD7C33", fontWeight: 600 }}>
            Feuille de route succession
          </div>
          <div style={{ fontFamily: "Spectral, serif", fontSize: 15, marginTop: 8, color: "rgba(246,242,233,0.8)", minHeight: 48 }}>
            Vous venez de perdre un proche ? Diagnostic gratuit + feuille de route personnalisée.
          </div>
          <div className="my-6">
            <span style={{ fontFamily: "Spectral, serif", fontSize: 46, fontWeight: 300, lineHeight: 1 }}>89,00 €</span>
            <span style={{ marginLeft: 6, fontSize: 15, color: "rgba(246,242,233,0.7)" }}>· une fois</span>
          </div>
          <ul className="space-y-3 flex-1 mb-6">
            {[
              "Diagnostic gratuit (5 min, sans compte)",
              "Feuille de route PDF envoyée par e-mail",
              "Ordre précis des professionnels à consulter",
              "Documents à réunir et actions à préparer",
              "Accès à vie via lien sécurisé personnel",
            ].map((f) => (
              <li key={f} className="flex items-start gap-3">
                <Check size={16} color="#AD7C33" style={{ flexShrink: 0, marginTop: 3 }} />
                <span style={{ fontSize: 14, color: "#F6F2E9", lineHeight: 1.45 }}>{f}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/ouvrir-une-succession/diagnostic"
            className="w-full"
            style={{
              padding: "12px 20px", fontSize: 15, fontWeight: 600, minHeight: 46,
              background: "#AD7C33", color: "#123B36",
              border: "none", cursor: "pointer",
              textDecoration: "none", textAlign: "center", display: "inline-block",
            }}
            data-testid="btn-choose-succession-feuille-route"
          >
            Commencer mon diagnostic
            <ArrowRight size={16} style={{ display: "inline", marginLeft: 8 }} />
          </Link>
        </div>
      </div>

      {/* CTA vers les Services sur mesure */}
      <div className="max-w-4xl mx-auto mt-16 text-center px-6" data-testid="pricing-services-cta">
        <div style={{ color: "var(--auprea-gold)", fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>
          — Envie d'aller plus loin ?
        </div>
        <h3 style={{ fontFamily: "Spectral, serif", fontSize: 26, color: "var(--auprea-navy)", fontWeight: 500, marginBottom: 10 }}>
          Découvrez nos prestations sur mesure
        </h3>
        <p style={{ fontSize: 15, color: "rgba(35,38,46,0.7)", marginBottom: 16, lineHeight: 1.6 }}>
          Consultation, atelier familial ou audit patrimonial complet — accompagnement humain à la carte, hors abonnement.
        </p>
        <Link to="/services" data-testid="link-services" className="auprea-btn-outline" style={{ display: "inline-block", padding: "10px 24px" }}>
          Voir les services sur mesure →
        </Link>
      </div>
    </div>
  );
}
